import { motion } from 'framer-motion';
import { Calendar, Download, FileText, Loader2, MapPin } from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useScreenLogging } from '../../hooks/useScreenLogging';
import { typography } from '../../styles/typography';
import { getAllSites } from '../SiteManagement/siteApi';
import { fdocApi } from '../Tax/FdocApi';

const getCurrentMonth = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
};

const getMonthDateRange = (yearMonth) => {
  if (!yearMonth || !/^\d{4}-\d{2}$/.test(yearMonth)) {
    const today = new Date();
    const y = today.getFullYear();
    const m = today.getMonth();
    const start = new Date(y, m, 1);
    const end = new Date();
    return {
      date_from: start.toISOString().split('T')[0],
      date_to: end.toISOString().split('T')[0],
    };
  }

  const [year, month] = yearMonth.split('-').map(Number);
  const start = new Date(year, month - 1, 1);
  const end = new Date(year, month, 0);
  return {
    date_from: start.toISOString().split('T')[0],
    date_to: end.toISOString().split('T')[0],
  };
};

const resolveSiteValue = (site) =>
  site?.site_id ?? site?.siteId ?? site?.id ?? site?._id ?? site?.site_code ?? site?.siteCode ?? '';

const encodeVendorFilterValue = (v) => {
  if (v.vendor_id) return `id:${v.vendor_id}`;
  if (v.vendor_name) return `name:${encodeURIComponent(v.vendor_name)}`;
  return '';
};

const encodeCustomerFilterValue = (c) => {
  if (c.customer_id) return `id:${c.customer_id}`;
  if (c.customer_name) return `name:${encodeURIComponent(c.customer_name)}`;
  return '';
};

const parseVendorFilterSelection = (value) => {
  if (!value) return {};
  if (value.startsWith('id:')) return { vendor_id: value.slice(3) };
  if (value.startsWith('name:')) return { vendor_name: decodeURIComponent(value.slice(5)) };
  return { vendor_id: value };
};

const parseCustomerFilterSelection = (value) => {
  if (!value) return {};
  if (value.startsWith('id:')) return { customer_id: value.slice(3) };
  if (value.startsWith('name:')) return { customer_name: decodeURIComponent(value.slice(5)) };
  return { customer_id: value };
};

const formatInr = (amount = 0) =>
  Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 2,
  }).format(Number(amount) || 0);

const formatDate = (d) => {
  if (!d) return '—';
  const x = new Date(d);
  return Number.isNaN(x.getTime())
    ? String(d)
    : x.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
};

const formatDateTime = (iso) => {
  if (!iso) return '—';
  const x = new Date(iso);
  return Number.isNaN(x.getTime())
    ? '—'
    : x.toLocaleString(undefined, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
};

const parseMoneyValue = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const normalized = String(value).replace(/,/g, '').match(/-?\d+(\.\d+)?/);
  if (!normalized) return null;
  const parsed = parseFloat(normalized[0]);
  return Number.isNaN(parsed) ? null : parsed;
};

const pickFirstMoney = (...values) => {
  for (const v of values) {
    const parsed = parseMoneyValue(v);
    if (parsed !== null) return parsed;
  }
  return null;
};

const getReferenceValue = (row, docDetails) => {
  const fields = docDetails?.extracted_data?.fields || {};
  return (
    row?.reference_number ||
    docDetails?.reference_number ||
    docDetails?.docNumber ||
    fields?.reference_number ||
    fields?.bill_no ||
    fields?.invoice_no ||
    fields?.po_number ||
    fields?.order_no ||
    fields?.serial_no ||
    fields?.document_number ||
    row?.file_name?.replace(/\.[^/.]+$/, '') ||
    row?.fdoc_id ||
    null
  );
};

const getAmountValue = (row, docDetails) => {
  const fields = docDetails?.extracted_data?.fields || {};
  const fromTotals = pickFirstMoney(
    row?.total_amount,
    docDetails?.total_amount,
    fields?.total_amount,
    fields?.grand_total,
    fields?.total,
    fields?.amount,
    fields?.payable_amount
  );
  if (fromTotals !== null) return fromTotals;

  const rawText = fields?.raw_text;
  if (typeof rawText === 'string') {
    const totalMatch = rawText.match(/TOTAL:\s*Rs\.?\s*([\d,]+(?:\.\d+)?)/i);
    if (totalMatch?.[1]) return parseMoneyValue(totalMatch[1]);
  }
  return null;
};

const getTaxValue = (row, docDetails) => {
  const fields = docDetails?.extracted_data?.fields || {};
  const directTax = pickFirstMoney(row?.tax_total, docDetails?.tax_total, fields?.tax_total, fields?.tax);
  if (directTax !== null) return directTax;

  const cgst = pickFirstMoney(fields?.cgst, fields?.cgst_2, fields?.cgst_25);
  const sgst = pickFirstMoney(fields?.sgst, fields?.sgst_3, fields?.sgst_25);
  const igst = pickFirstMoney(fields?.igst);
  if (cgst !== null || sgst !== null || igst !== null) {
    return (cgst || 0) + (sgst || 0) + (igst || 0);
  }

  const rawText = fields?.raw_text;
  if (typeof rawText === 'string') {
    const cgstMatch = rawText.match(/CGST[^:\n]*:\s*Rs\.?\s*([\d,]+(?:\.\d+)?)/i);
    const sgstMatch = rawText.match(/SGST[^:\n]*:\s*Rs\.?\s*([\d,]+(?:\.\d+)?)/i);
    const igstMatch = rawText.match(/IGST[^:\n]*:\s*Rs\.?\s*([\d,]+(?:\.\d+)?)/i);
    const cgstRaw = cgstMatch?.[1] ? parseMoneyValue(cgstMatch[1]) : null;
    const sgstRaw = sgstMatch?.[1] ? parseMoneyValue(sgstMatch[1]) : null;
    const igstRaw = igstMatch?.[1] ? parseMoneyValue(igstMatch[1]) : null;
    if (cgstRaw !== null || sgstRaw !== null || igstRaw !== null) {
      return (cgstRaw || 0) + (sgstRaw || 0) + (igstRaw || 0);
    }
  }
  return null;
};

const getDownloadUrl = (row, docDetails) =>
  row?.download_url ||
  row?.file_url ||
  row?.document_url ||
  row?.url ||
  docDetails?.download_url ||
  null;

const OrderHistoryScreen = () => {
  useScreenLogging('OrderHistory', { component: 'OrderHistoryScreen' });

  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth);
  const [sites, setSites] = useState([]);
  const [sitesLoading, setSitesLoading] = useState(false);
  const [selectedSite, setSelectedSite] = useState(null);
  const [partyKind, setPartyKind] = useState('both');
  const [listKind, setListKind] = useState('vendor');
  const [filters, setFilters] = useState({ vendors: [], customers: [] });
  const [vendorSel, setVendorSel] = useState('');
  const [customerSel, setCustomerSel] = useState('');
  const [entries, setEntries] = useState([]);
  const [entryDetailsById, setEntryDetailsById] = useState({});
  const [summary, setSummary] = useState({ total_amount_sum: 0, tax_total_sum: 0 });
  const [lastKey, setLastKey] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [downloadingById, setDownloadingById] = useState({});
  const fetchedDetailIdsRef = useRef(new Set());

  const effectiveKind = useMemo(() => {
    if (partyKind === 'both') return listKind;
    return partyKind === 'customer' ? 'customer' : 'vendor';
  }, [partyKind, listKind]);

  useEffect(() => {
    if (partyKind === 'vendor') setListKind('vendor');
    else if (partyKind === 'customer') setListKind('customer');
  }, [partyKind]);

  useEffect(() => {
    const loadSites = async () => {
      setSitesLoading(true);
      try {
        const response = await getAllSites();
        const sitesList = Array.isArray(response?.sites)
          ? response.sites
          : Array.isArray(response)
            ? response
            : Array.isArray(response?.data)
              ? response.data
              : [];
        const sorted = [...sitesList].sort((a, b) =>
          (a?.site_name || a?.name || '').localeCompare(b?.site_name || b?.name || '')
        );
        setSites(sorted);
      } catch (e) {
        console.warn('[OrderHistory] Failed to load sites:', e?.message || e);
        setSites([]);
      } finally {
        setSitesLoading(false);
      }
    };
    loadSites();
  }, []);

  useEffect(() => {
    const loadFilters = async () => {
      try {
        const { date_from, date_to } = getMonthDateRange(selectedMonth);
        const siteId = selectedSite ? resolveSiteValue(selectedSite) : null;
        const result = await fdocApi.getOrderHistoryFilters({
          kind: partyKind,
          date_from,
          date_to,
          status: 'confirmed,extracted',
          site_id: siteId || undefined,
          limit: 200,
        });
        setFilters({
          vendors: result.vendors || [],
          customers: result.customers || [],
        });
      } catch (err) {
        console.warn('[OrderHistory] Filters:', err?.message || err);
        setFilters({ vendors: [], customers: [] });
      }
    };
    loadFilters();
  }, [partyKind, selectedMonth, selectedSite]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const { date_from, date_to } = getMonthDateRange(selectedMonth);
        const siteId = selectedSite ? resolveSiteValue(selectedSite) : null;
        const params = {
          kind: effectiveKind,
          date_from,
          date_to,
          status: 'confirmed,extracted',
          limit: 50,
        };
        if (siteId) params.site_id = siteId;
        if (effectiveKind === 'vendor' && vendorSel) {
          Object.assign(params, parseVendorFilterSelection(vendorSel));
        }
        if (effectiveKind === 'customer' && customerSel) {
          Object.assign(params, parseCustomerFilterSelection(customerSel));
        }
        const res = await fdocApi.getOrderHistory(params);
        if (cancelled) return;
        setEntries(Array.isArray(res.entries) ? res.entries : []);
        setEntryDetailsById({});
        fetchedDetailIdsRef.current = new Set();
        setSummary(res.summary || { total_amount_sum: 0, tax_total_sum: 0 });
        setLastKey(res.last_key ?? null);
      } catch (e) {
        if (!cancelled) {
          setError(e.message || 'Failed to load FDOC order history.');
          setEntries([]);
          setSummary({ total_amount_sum: 0, tax_total_sum: 0 });
          setLastKey(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [selectedMonth, selectedSite, effectiveKind, vendorSel, customerSel]);

  const loadMore = async () => {
    if (!lastKey || loadingMore) return;
    setLoadingMore(true);
    try {
      const { date_from, date_to } = getMonthDateRange(selectedMonth);
      const siteId = selectedSite ? resolveSiteValue(selectedSite) : null;
      const lastKeyStr =
        typeof lastKey === 'string' ? lastKey : JSON.stringify(lastKey);
      const params = {
        kind: effectiveKind,
        date_from,
        date_to,
        status: 'confirmed,extracted',
        limit: 50,
        last_key: lastKeyStr,
      };
      if (siteId) params.site_id = siteId;
      if (effectiveKind === 'vendor' && vendorSel) {
        Object.assign(params, parseVendorFilterSelection(vendorSel));
      }
      if (effectiveKind === 'customer' && customerSel) {
        Object.assign(params, parseCustomerFilterSelection(customerSel));
      }
      const res = await fdocApi.getOrderHistory(params);
      setEntries((prev) => [...prev, ...(Array.isArray(res.entries) ? res.entries : [])]);
      setLastKey(res.last_key ?? null);
      if (res.summary) setSummary(res.summary);
    } catch (e) {
      console.warn('[OrderHistory] Load more failed:', e?.message || e);
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const missingRows = entries.filter((row) => {
      if (!row?.fdoc_id || fetchedDetailIdsRef.current.has(row.fdoc_id)) return false;
      return row.reference_number == null || row.total_amount == null || row.tax_total == null;
    });

    if (missingRows.length === 0) return () => { cancelled = true; };

    const loadMissingDetails = async () => {
      const results = await Promise.allSettled(
        missingRows.map(async (row) => {
          fetchedDetailIdsRef.current.add(row.fdoc_id);
          const details = await fdocApi.getDocument(row.fdoc_id);
          return { fdoc_id: row.fdoc_id, details };
        })
      );

      if (cancelled) return;
      const next = {};
      results.forEach((result) => {
        if (result.status === 'fulfilled' && result.value?.fdoc_id) {
          next[result.value.fdoc_id] = result.value.details;
        }
      });
      if (Object.keys(next).length > 0) {
        setEntryDetailsById((prev) => ({ ...prev, ...next }));
      }
    };

    loadMissingDetails();
    return () => {
      cancelled = true;
    };
  }, [entries]);

  const filteredEntries = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return entries;
    return entries.filter((row) => {
      const ref = String(row.reference_number || '').toLowerCase();
      const fn = String(row.file_name || '').toLowerCase();
      const vn = String(row.vendor?.vendor_name || '').toLowerCase();
      const cn = String(row.customer?.customer_name || '').toLowerCase();
      const dt = String(row.document_type || '').toLowerCase();
      return (
        ref.includes(q) ||
        fn.includes(q) ||
        vn.includes(q) ||
        cn.includes(q) ||
        dt.includes(q)
      );
    });
  }, [entries, searchQuery]);

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  const handleDownload = async (row) => {
    if (!row?.fdoc_id) return;
    const currentDetails = entryDetailsById[row.fdoc_id];
    let url = getDownloadUrl(row, currentDetails);

    if (!url) {
      setDownloadingById((prev) => ({ ...prev, [row.fdoc_id]: true }));
      try {
        const details = await fdocApi.getDocument(row.fdoc_id);
        setEntryDetailsById((prev) => ({ ...prev, [row.fdoc_id]: details }));
        url = getDownloadUrl(row, details);
      } catch (e) {
        setError(e?.message || 'Failed to generate download link.');
      } finally {
        setDownloadingById((prev) => ({ ...prev, [row.fdoc_id]: false }));
      }
    }

    if (!url) {
      setError('Download link not available for this file.');
      return;
    }

    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="w-full min-h-screen">
      <div className="mx-auto h-auto px-1">
        <motion.div
          variants={cardVariants}
          initial="hidden"
          animate="visible"
          className="space-y-8"
        >
          <div className="mb-2">
            <h1 className={typography.h2} style={{ color: '#000000' }}>
              Order history
            </h1>
            <p className="mt-2 text-gray-600">
              FDOC documents (vendor and customer views) for the selected period.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            <div>
              <label
                htmlFor="oh-party"
                className="mb-2 inline-flex items-center gap-2 text-sm font-semibold text-gray-700"
              >
                <FileText className="h-4 w-4" />
                Party filter
              </label>
              <select
                id="oh-party"
                value={partyKind}
                onChange={(e) => setPartyKind(e.target.value)}
                className="w-full min-h-[48px] rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100"
              >
                <option value="vendor">Vendor</option>
                <option value="customer">Customer</option>
                <option value="both">Both</option>
              </select>
            </div>
            {partyKind === 'both' && (
              <div>
                <label
                  htmlFor="oh-list-kind"
                  className="mb-2 block text-sm font-semibold text-gray-700"
                >
                  List view
                </label>
                <select
                  id="oh-list-kind"
                  value={listKind}
                  onChange={(e) => setListKind(e.target.value)}
                  className="w-full min-h-[48px] rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100"
                >
                  <option value="vendor">Vendor (PO / Estimation)</option>
                  <option value="customer">Customer (Invoice / Bill)</option>
                </select>
              </div>
            )}
            <div>
              <label
                htmlFor="oh-month"
                className="mb-2 inline-flex items-center gap-2 text-sm font-semibold text-gray-700"
              >
                <Calendar className="h-4 w-4" />
                Month
              </label>
              <input
                id="oh-month"
                type="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="w-full min-h-[48px] rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100"
              />
            </div>
            <div>
              <label
                htmlFor="oh-site"
                className="mb-2 inline-flex items-center gap-2 text-sm font-semibold text-gray-700"
              >
                <MapPin className="h-4 w-4" />
                Site (optional)
              </label>
              <select
                id="oh-site"
                value={selectedSite ? resolveSiteValue(selectedSite) : ''}
                onChange={(e) => {
                  const id = e.target.value;
                  setSelectedSite(id ? sites.find((s) => resolveSiteValue(s) === id) || null : null);
                }}
                className="w-full min-h-[48px] rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100"
                disabled={sitesLoading}
              >
                <option value="">{sitesLoading ? 'Loading sites…' : 'All sites'}</option>
                {sites.map((site) => (
                  <option key={resolveSiteValue(site)} value={resolveSiteValue(site)}>
                    {site.site_name || site.name || 'Site'}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {effectiveKind === 'vendor' && (
              <div>
                <label htmlFor="oh-vendor" className="mb-2 block text-sm font-semibold text-gray-700">
                  Vendor
                </label>
                <select
                  id="oh-vendor"
                  value={vendorSel}
                  onChange={(e) => setVendorSel(e.target.value)}
                  className="w-full min-h-[48px] rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100"
                >
                  <option value="">All vendors</option>
                  {filters.vendors.map((v) => (
                    <option key={encodeVendorFilterValue(v) || v.vendor_name} value={encodeVendorFilterValue(v)}>
                      {v.vendor_name || v.vendor_id || 'Vendor'}{' '}
                      {v.count != null ? `(${v.count})` : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {effectiveKind === 'customer' && (
              <div>
                <label htmlFor="oh-customer" className="mb-2 block text-sm font-semibold text-gray-700">
                  Customer
                </label>
                <select
                  id="oh-customer"
                  value={customerSel}
                  onChange={(e) => setCustomerSel(e.target.value)}
                  className="w-full min-h-[48px] rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100"
                >
                  <option value="">All customers</option>
                  {filters.customers.map((c) => (
                    <option
                      key={encodeCustomerFilterValue(c) || c.customer_name}
                      value={encodeCustomerFilterValue(c)}
                    >
                      {c.customer_name || c.customer_id || 'Customer'}{' '}
                      {c.count != null ? `(${c.count})` : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="md:col-span-2">
              <label htmlFor="oh-search" className="mb-2 block text-sm font-semibold text-gray-700">
                Search (this page)
              </label>
              <input
                id="oh-search"
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Reference, file name, party…"
                className="w-full min-h-[48px] rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 placeholder:text-gray-400 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100"
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-6 text-sm text-gray-700">
            <span>
              Total amount (filtered): <strong>{formatInr(summary.total_amount_sum)}</strong>
            </span>
            <span>
              Tax total: <strong>{formatInr(summary.tax_total_sum)}</strong>
            </span>
          </div>

          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">{error}</div>
          )}

          <div className="rounded-3xl">
            <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm">
              <table className="min-w-full border-collapse text-sm md:text-base">
                <thead>
                  <tr className="bg-gray-50 text-left text-gray-600">
                    <th className="min-w-[120px] px-4 py-3 font-semibold">Reference</th>
                    <th className="min-w-[100px] px-4 py-3 font-semibold">Type</th>
                    <th className="min-w-[110px] px-4 py-3 font-semibold">Doc date</th>
                    <th className="min-w-[90px] px-4 py-3 font-semibold">Status</th>
                    <th className="min-w-[140px] px-4 py-3 font-semibold">Vendor</th>
                    <th className="min-w-[140px] px-4 py-3 font-semibold">Customer</th>
                    <th className="min-w-[110px] px-4 py-3 font-semibold text-right">Amount</th>
                    <th className="min-w-[100px] px-4 py-3 font-semibold text-right">Tax</th>
                    <th className="min-w-[160px] px-4 py-3 font-semibold">Uploaded</th>
                    <th className="min-w-[120px] px-4 py-3 font-semibold">Download</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={10} className="px-4 py-12 text-center text-gray-500">
                        <Loader2 className="mx-auto mb-2 h-8 w-8 animate-spin text-green-600" />
                        Loading…
                      </td>
                    </tr>
                  ) : filteredEntries.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="px-4 py-8 text-center text-gray-500">
                        No FDOC documents match the current filters.
                      </td>
                    </tr>
                  ) : (
                    filteredEntries.map((row) => (
                      <tr key={row.fdoc_id} className="border-t border-gray-100 hover:bg-gray-50/50">
                        <td className="px-4 py-3 text-gray-800">
                          {getReferenceValue(row, entryDetailsById[row.fdoc_id]) || '—'}
                        </td>
                        <td className="px-4 py-3 text-gray-800">{row.document_type || '—'}</td>
                        <td className="px-4 py-3 text-gray-800">{formatDate(row.document_date)}</td>
                        <td className="px-4 py-3 text-gray-800">{row.status || '—'}</td>
                        <td className="px-4 py-3 text-gray-800">
                          {row.vendor?.vendor_name || row.vendor?.vendor_id || '—'}
                        </td>
                        <td className="px-4 py-3 text-gray-800">
                          {row.customer?.customer_name || row.customer?.customer_id || '—'}
                        </td>
                        <td className="px-4 py-3 text-right text-gray-800">
                          {getAmountValue(row, entryDetailsById[row.fdoc_id]) != null
                            ? formatInr(getAmountValue(row, entryDetailsById[row.fdoc_id]))
                            : '—'}
                        </td>
                        <td className="px-4 py-3 text-right text-gray-800">
                          {getTaxValue(row, entryDetailsById[row.fdoc_id]) != null
                            ? formatInr(getTaxValue(row, entryDetailsById[row.fdoc_id]))
                            : '—'}
                        </td>
                        <td className="px-4 py-3 text-gray-600">{formatDateTime(row.uploaded_at)}</td>
                        <td className="px-4 py-3">
                          <button
                            type="button"
                            onClick={() => handleDownload(row)}
                            disabled={Boolean(downloadingById[row.fdoc_id])}
                            className="inline-flex items-center gap-2 rounded-md border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            {downloadingById[row.fdoc_id] ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Download className="h-4 w-4" />
                            )}
                            Download
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {lastKey && !loading && (
            <div className="flex justify-center pb-8">
              <button
                type="button"
                onClick={loadMore}
                disabled={loadingMore}
                className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-6 py-2 text-sm font-semibold text-gray-800 shadow-sm transition hover:bg-gray-50 disabled:opacity-50"
              >
                {loadingMore ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin text-green-600" />
                    Loading…
                  </>
                ) : (
                  'Load more'
                )}
              </button>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default OrderHistoryScreen;
